import React, { useState, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import Avatar from "../../../../common/components/Avatar";
import Close from "@mui/icons-material/Close";
import styles from "./Teammate.module.scss";
import { useTeamFetcher } from "../../../../utils/TeamsFetcherHooks";
import { useProjectFetcher } from "../../../../utils/ProjectsFetcherHooks";
import TeamService from "../../../../service/TeamService";
import DeleteModal from "../../../../components/DeleteModal";
import { ROUTES } from "../../../../utils/Constant";
import { routeFormatter } from "../../../../utils/Formatter";

const Teammate = (props) => {
  const { t } = useTranslation();
  const history = useHistory();

  const {
    canDelete,
    isCurrentUser,
    first_name,
    last_name,
    email,
    canShowEmail,
    roles,
    teamId,
    teammateUserId,
  } = props;

  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const { fetchMyTeamsAndUpdateStore } = useTeamFetcher();
  const { fetchMyProjectsAndUpdateStore } = useProjectFetcher();

  const onDeleteClickClose = (e) => {
    e.preventDefault();
    e.stopPropagation();

    setShowDeleteModal(false);
  };

  const onDelete = useCallback(async () => {
    try {
      await TeamService.removeTeammate({
        teamId: teamId,
        teammateId: teammateUserId,
      });
      //Need to wait for update of teams, in case the team that was removed is on top of the teams array
      //(because redirecting to TEAMS will redirect to the first team of the array...)
      await fetchMyTeamsAndUpdateStore();
      await fetchMyProjectsAndUpdateStore();

      //If we just removed ourselves from a team, then we must redirect to general page
      if (isCurrentUser) {
        history.push(routeFormatter(ROUTES.HOME));
      }
    } catch (e) {
      console.error(e);
    } finally {
      setShowDeleteModal(false);
    }
  }, [
    teamId,
    teammateUserId,
    fetchMyTeamsAndUpdateStore,
    fetchMyProjectsAndUpdateStore,
    history,
    isCurrentUser,
  ]);

  const initials = useMemo(() => first_name[0], [first_name]);

  const label = useMemo(() => {
    if (isCurrentUser) {
      return `${first_name} ${last_name} (${t("team.you")})`;
    }

    if (
      roles &&
      roles.find(
        (roleElement) =>
          roleElement.role === "designer" || roleElement.role === "admin"
      )
    ) {
      return `${first_name} ${last_name} (${t("team.jameo")})`;
    }

    if (canShowEmail) {
      return `${first_name} ${last_name} (${email})`;
    }

    return `${first_name} ${last_name}`;
  }, [first_name, last_name, t, isCurrentUser, roles, canShowEmail, email]);

  return (
    <div className={styles.container}>
      <Avatar initials={initials} />
      <span className={styles.name}>{label}</span>
      <div style={{ flex: 1 }} />
      {canDelete && (
        <div
          className={styles.removeButton}
          onClick={() => {
            setShowDeleteModal(true);
          }}
        >
          <Close />
        </div>
      )}
      <DeleteModal
        open={showDeleteModal}
        onDelete={onDelete}
        title={t("project.remove_teammate_modal", { first_name, last_name })}
        deleteButtomLabel={t("project.remove_menu")}
        onClickClose={onDeleteClickClose}
      />
    </div>
  );
};

export default Teammate;
